import { bytesToBase64, hexToBytes } from '@/utils';
import { BaseSecurityCrypto } from './base';
import { logGenerateKeys, logSign } from '@byted/tiktok-zti-monitor';

/**
 * 定义一个接口，用于构造时传入 PEM 格式的密钥（可选）
 */
export interface CryptoProps {
  privateKey?: string; // PEM 格式的私钥
  publicKey?: string; // PEM 格式的公钥
}

/**
 * 把 32 字节的数值 (r 或 s) 转为 DER 编码的 INTEGER：
 * 1) 去前导 0x00
 * 2) 若最高位是 1 => 前面补 0x00 (DER 整数是有符号表示的)
 */
function toDERInteger(integerBytes: Uint8Array): Uint8Array {
  let i = 0;
  while (i < integerBytes.length - 1 && integerBytes[i] === 0) {
    i++;
  }
  let der = integerBytes.slice(i);

  // 若最高位 bit 为 1，需要在前补一个 0x00，防止被当成负数
  if (der[0] & 0x80) {
    const extended = new Uint8Array(der.length + 1);
    extended[0] = 0x00;
    extended.set(der, 1);
    der = extended;
  }
  return der;
}

/**
 * 将 (r + s) raw 格式 (64字节) 转为 ASN.1 DER 格式
 * r, s 各32字节 => DER 有头部等 => 最终大小通常在 70~72 字节左右
 */
function rawSigToDER(signature: Uint8Array): Uint8Array {
  if (signature.length !== 64) {
    throw new Error('ECDSA (P-256) raw signature must be 64 bytes');
  }

  const r = signature.slice(0, 32);
  const s = signature.slice(32);

  const rDer = toDERInteger(r);
  const sDer = toDERInteger(s);

  // 序列: 0x30, totalLength, 0x02, rLen, rDer, 0x02, sLen, sDer
  const totalLength = rDer.length + sDer.length + 4; // 4 = 两个(0x02+length)
  const derSignature = new Uint8Array(2 + totalLength);

  derSignature[0] = 0x30; // SEQUENCE
  derSignature[1] = totalLength;

  // 写入 r
  derSignature[2] = 0x02; // INTEGER
  derSignature[3] = rDer.length;
  derSignature.set(rDer, 4);

  // 写入 s
  const sOffset = 4 + rDer.length;
  derSignature[sOffset] = 0x02;
  derSignature[sOffset + 1] = sDer.length;
  derSignature.set(sDer, sOffset + 2);

  return derSignature;
}

/**
 * 主类：SecurityCrypto
 */
class SecurityCrypto extends BaseSecurityCrypto {
  private _cached_private_key: CryptoKey | undefined;

  constructor(props: CryptoProps) {
    super(props);
    const { privateKey, publicKey } = props;
    if (privateKey && publicKey) {
      // 若用户传入，则直接使用
      this._private_key = privateKey;
      this._public_key = publicKey;
      this.importPublicKey(publicKey).then((key) => {
        this.exportPublicKey(key);
      });
    } else {
      this.generateKeyPair();
    }
  }

  /**
   * 生成新的 ECDSA (P-256) 密钥对并保存为 PEM
   * 注意：这是一个异步方法
   */
  async generateKeyPair(): Promise<void> {
    const start = Date.now();

    const keyPair = await window.crypto.subtle.generateKey(
      {
        name: 'ECDSA',
        namedCurve: 'P-256'
      },
      true,
      ['sign', 'verify']
    );

    this._private_key = await this.exportPrivateKey(keyPair.privateKey);
    this._public_key = await this.exportPublicKey(keyPair.publicKey);
    logGenerateKeys({
      duration: Date.now() - start,
      type: 'crypto'
    });
  }

  /**
   * 获取当前保存在内存中的 PEM 格式公钥、私钥
   */
  getKeys() {
    return {
      publicKey: this._public_key,
      privateKey: this._private_key,
      publicRawKeyBase64: this.publicRawKeyBase64
    };
  }

  /**
   * 用私钥对指定字符串做 ECDSA (SHA-256) 签名
   * 并返回 DER 编码后的 HEX 字符串
   */
  async sign(sign_str: string): Promise<string> {
    if (!this._private_key) {
      await this.generateKeyPair();
    }
    if (!this._private_key) {
      throw new Error('Private key is not available. (Did you call generateKeyPair() ?)');
    }
    const start = Date.now();
    // 将 PEM 格式导入 CryptoKey
    const privateKey = await this.importPrivateKey(this._private_key);

    // 签名
    const encoder = new TextEncoder();
    const data = encoder.encode(sign_str);
    const rawSignature = await window.crypto.subtle.sign(
      { name: 'ECDSA', hash: { name: 'SHA-256' } },
      privateKey,
      data
    );

    // raw => DER => hex
    const derBytes = rawSigToDER(new Uint8Array(rawSignature));
    const signData = this.bufferToHex(derBytes);
    logSign({
      duration: Date.now() - start,
      type: 'crypto'
    });
    return signData;
  }

  /**
   * 导出私钥 (CryptoKey => PEM)
   */
  private async exportPrivateKey(privateKey: CryptoKey): Promise<string> {
    const pkcs8 = await window.crypto.subtle.exportKey('pkcs8', privateKey);
    return this.arrayBufferToPem(pkcs8, 'PRIVATE KEY');
  }

  /**
   * 导出公钥 (CryptoKey => PEM)
   */
  private async exportPublicKey(publicKey: CryptoKey): Promise<string> {
    const spki = await window.crypto.subtle.exportKey('spki', publicKey);

    const publicKeyBytes = new Uint8Array(spki);
    // 去掉 SPKI 头部，只保留公钥数据
    const rawKey = publicKeyBytes.slice(-65); // P-256

    const hex = this.bufferToHex(rawKey);
    const bytes = hexToBytes(hex);
    this.publicRawKeyBase64 = bytesToBase64(bytes);

    return this.arrayBufferToPem(spki, 'PUBLIC KEY');
  }

  /**
   * 导入 PEM 格式私钥 => CryptoKey
   */
  private async importPrivateKey(pem: string): Promise<CryptoKey> {
    if (!this._cached_private_key) {
      const pemHeader = '-----BEGIN PRIVATE KEY-----';
      const pemFooter = '-----END PRIVATE KEY-----';
      const pemContents = pem.replace(pemHeader, '').replace(pemFooter, '').replace(/\s+/g, '');

      const binaryDer = this.base64ToArrayBuffer(pemContents);
      this._cached_private_key = await window.crypto.subtle.importKey(
        'pkcs8',
        binaryDer,
        { name: 'ECDSA', namedCurve: 'P-256' },
        false,
        ['sign']
      );
    }
    return this._cached_private_key;
  }

  /**
   * 导入 PEM 格式公钥 => CryptoKey
   */
  private importPublicKey(pem: string): Promise<CryptoKey> {
    const pemHeader = '-----BEGIN PUBLIC KEY-----';
    const pemFooter = '-----END PUBLIC KEY-----';
    const pemContents = pem.replace(pemHeader, '').replace(pemFooter, '').replace(/\s+/g, '');

    const binaryDer = this.base64ToArrayBuffer(pemContents);
    return window.crypto.subtle.importKey('spki', binaryDer, { name: 'ECDSA', namedCurve: 'P-256' }, true, [
      'verify'
    ]);
  }

  /**
   * 将 ArrayBuffer 转为 PEM 格式
   */
  private arrayBufferToPem(buffer: ArrayBuffer, label: string): string {
    const base64 = this.arrayBufferToBase64(buffer);
    const pem = `-----BEGIN ${label}-----\n${base64}\n-----END ${label}-----`;
    return pem;
  }

  /**
   * ArrayBuffer => Base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Base64 => ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * 将 ArrayBuffer 转为 HEX 字符串
   */
  private bufferToHex(data: ArrayBuffer | Uint8Array): string {
    let bytes: Uint8Array;

    if (data instanceof ArrayBuffer) {
      // 如果传进来的是 ArrayBuffer
      bytes = new Uint8Array(data);
    } else {
      // 如果传进来的是 Uint8Array，直接用
      bytes = data;
    }

    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
}

export default SecurityCrypto;
